<template>
  <div class="widget-body" ref="widgetBody">
    <c-loader v-if="isLoading" />
    <v-alert show v-if="isNoData" variant="light" align="center">{{ $t('white-label.globalMessages.nodata') }}</v-alert>
    <b-table-lite
      v-else-if="!hasError"
      :fields="fields"
      :items="tsData"
      bordered
      dark
      hover
      small
      sticky-header
      striped
      thead-class="text-center"
      tbody-tr-class="text-right"
      :aria-label="$t(`${i18nNamespace}.widget-title`)"
    >
      <template v-for="field in fields" #[`head(${field.key})`]>
        {{ $t(`${i18nNamespace}.table.header.${field.key}`) }}
      </template>

      <template #cell(price)="data">
        <v-fidlet :data="data.value || ''" :type="metadata['tns.6'].type" />
      </template>
      <template #cell(size)="data">
        <v-fidlet :data="data.value || ''" :type="metadata['tns.178'].type" />
      </template>
      <template v-slot:cell(conditions)="data">
        <v-fidlet
          :data="data.value || ''"
          :type="metadataType('tns.1003') || metadataType('tns.3264') || metadataType('tns.131')"
        />
      </template>
      <template v-slot:cell(buyerId)="data">
        <v-fidlet :data="data.value || ''" :type="metadataType('tns.162')" />
      </template>
      <template v-slot:cell(sellerId)="data">
        <v-fidlet :data="data.value || ''" :type="metadataType('tns.163')" />
      </template>
      <template v-slot:cell(info)="data">
        <v-fidlet :data="data.value || ''" :type="metadataType('tns.1018') || metadataType('tns.1631')" />
      </template>
    </b-table-lite>

    <div class="text-center p-2" v-if="hasError">{{ $t(`${i18nNamespace}.error`) }}</div>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import { API } from '@/state/api';
import moment from 'moment';
import { mapCondition } from './conditionMapping';

import { toTimezoneDate } from '@/utils/date';

export default {
  name: 'w-times-and-sales-widget',
  components: {
    CLoader
  },
  props: {
    ric: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      isNoData: false,
      hasError: false,
      results: 100,
      fieldOptions: {
        date: {
          key: 'date',
          tdClass: 'text-left',
          formatter: value =>
            moment(toTimezoneDate(this.timezone, value))
              .format('MMM DD')
              .toUpperCase()
        },
        time: {
          key: 'time',
          formatter: value => this.$f(value, 'time.hms')
        },
        price: {
          key: 'price'
        },
        size: {
          key: 'size'
        },
        conditions: {
          key: 'conditions',
          tdClass: 'text-left'
        },
        buyerId: {
          key: 'buyerId'
        },
        sellerId: {
          key: 'sellerId'
        },
        info: {
          key: 'info',
          tdClass: 'text-left'
        }
      },
      lastValidTimestamp: null,
      tsData: [],
      metadata: {}
    };
  },
  computed: {
    i18nNamespace() {
      return 'white-label.widgets.timesAndSales';
    },
    locale() {
      return this.$store.state.applicationSettings.general.settings.applicationLanguage;
    },
    ricColumnSet() {
      const ric = this.ric;
      if (ric.endsWith('.TO') || ric.endsWith('.V')) {
        return 'CA';
      }
      if (
        ric.endsWith('.N') ||
        ric.endsWith('.O') ||
        ric.endsWith('.OQ') ||
        ric.endsWith('.A') ||
        ric.endsWith('.K') ||
        ric.indexOf('.') <= 0 // no suffix
      ) {
        return 'US';
      }
      return 'OTHER';
    },
    fids() {
      if (this.ricColumnSet === 'CA') {
        return [0, 6, 178, 3264, 162, 163, 7053];
      }
      if (this.ricColumnSet === 'US') {
        return [0, 6, 178, 1003, 7053];
      }
      return [0, 6, 1631, 178, 131, 7053];
    },
    fields() {
      if (this.ricColumnSet === 'CA') {
        return [
          this.fieldOptions.date,
          this.fieldOptions.time,
          this.fieldOptions.price,
          this.fieldOptions.size,
          this.fieldOptions.conditions,
          this.fieldOptions.buyerId,
          this.fieldOptions.sellerId
        ];
      }
      if (this.ricColumnSet === 'US') {
        return [
          this.fieldOptions.date,
          this.fieldOptions.time,
          this.fieldOptions.price,
          this.fieldOptions.size,
          this.fieldOptions.conditions
        ];
      }
      return [
        this.fieldOptions.date,
        this.fieldOptions.time,
        this.fieldOptions.price,
        this.fieldOptions.info,
        this.fieldOptions.size,
        this.fieldOptions.conditions
      ];
    },
    timezone() {
      return this.$store.state.applicationSettings.general.settings.timezone;
    }
  },
  watch: {
    ric: {
      handler() {
        this.lastValidTimestamp = null;

        this.scrollToTop();
        this.fetch();
      }
    },
    locale: {
      handler(newValue) {
        moment.locale(newValue);
      },
      immediate: true
    }
  },
  methods: {
    metadataType(fid) {
      return this.metadata[fid] ? this.metadata[fid].type : undefined;
    },
    onSetRIC(ric) {
      this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, ric);
    },
    processData(data) {
      // removing unnecessary items from new data based on lastValidTimestamp (from 'last new data'/current data)
      if (this.lastValidTimestamp) {
        const lastTimestampIndex = this.tsData.findIndex(x => x.ts == this.lastValidTimestamp);
        const ignorableRows = this.tsData.length - 1 - lastTimestampIndex;

        if (ignorableRows > 0) {
          data.splice(data.length - ignorableRows, ignorableRows);
        }
      }

      const enrichedData = data
        .map(o => {
          o = this._.mapKeys(o, (_val, key) => key.replace(/^tns\./, ''));
          const conditions = o['1003'] || o['3264'] || o['131'];
          const size = conditions === 'DRK' ? o['7053'] : o['178'];
          return {
            date: parseFloat(o['0']),
            time: parseFloat(o['0']),
            price: o['6'],
            size: size,
            ts: o['0'],
            conditions: mapCondition(conditions),
            buyerId: o['162'],
            sellerId: o['163'],
            info: o['1018'] || o['1631']
          };
        })
        .filter(o => o.size !== undefined);

      return enrichedData;
    },
    getLastValidTimestamp() {
      return Math.min.apply(
        Math,
        this.tsData.map(function(o) {
          return o.ts;
        })
      );
    },
    fetch() {
      this.hasError = false;
      this.isNoData = false;
      this.isLoading = true;

      this.getTNSData()
        .then(response => {
          if (response.data.data.length > 1 && Object.keys(response.data.data[0]).length > 0) {
            this.metadata = response.data.metadata;

            this.tsData = this.lastValidTimestamp
              ? this.tsData.concat(this.processData(response.data.data))
              : this.processData(response.data.data);
            this.lastValidTimestamp = this.getLastValidTimestamp();
          } else {
            // error case / no data
            this.isNoData = true;
            this.tsData = [];
          }

          this.isLoading = false;
        })
        .catch(e => {
          this.isLoading = false;
          this.hasError = true;
          this.tsData = [];
          console.log(e);
        });
    },
    getTNSData() {
      const lastTimestamp = this.lastValidTimestamp ? '&toDate=' + this.lastValidTimestamp : '';
      const fidsQuery = 'tns.' + this.fids.join(',tns.');
      return API.Rest.get(`/timeseries/tns?ric=${this.ric}&fids=${fidsQuery}${lastTimestamp}&count=${this.results}`);
    },
    update() {
      this.lastValidTimestamp = null;
      this.scrollToTop();
      this.fetch();
    },
    scrollToTop() {
      this.$refs.widgetBody.scrollTop = 0;
    }
  },
  mounted() {
    this.$refs.widgetBody.onscroll = e => {
      if (e.target.scrollTop >= e.target.scrollHeight - e.target.offsetHeight - 3) {
        if (!this.isLoading) {
          this.fetch();
        }
      }
    };

    this.fetch();
  },
  beforeDestroy() {
    this.$refs.widgetBody.onscroll = null;
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/times-and-sales-widget';
</style>
